function chartColors(colors, lineOrder, barCount, lineCount) {
    const usedColors: any = [];
    const hasBarsAndLines = !!barCount && !!lineCount;
    let bars: any = [];
    let lines: any = [];
    if (hasBarsAndLines) {
        for (let i = 0; i < lineCount; i++) {
            const colorIndex = lineOrder[i];
            usedColors.push(colorIndex);
            lines.push(lineColor(colors[colorIndex]));
        }

        for (let i = 0; i < barCount; i++) {
            let colorIndex = i;
            while (usedColors.indexOf(colorIndex) > -1) {
                colorIndex++;
                if (colorIndex >= colors.length) {
                    throw new Error('Unable to retrieve colors');
                }
            }

            usedColors.push(colorIndex);
            bars.push(barColor(colors[colorIndex]));
        }
    } else if (barCount) {
        bars = colors.slice(0, barCount).map((color) => barColor(color));
    } else {
        lines = colors.slice(0, lineCount).map((color) => lineColor(color));
    }

    return {
        bars,
        lines
    };
}

function barColor(color) {
    return Array.isArray(color) ? color[0] : color;
}

function lineColor(color) {
    return Array.isArray(color) ? color[1] : color;
}

class ColorPicker {
    lines: any;
    bars: any;

    constructor(
        public colors,
        public lineOrder,
        public barCount = 0,
        public lineCount = 0
    ) {
        const { lines, bars } = chartColors(colors, lineOrder, barCount, lineCount);
        this.lines = lines;
        this.bars = bars;
    }

    with(count) {
        return this.and(count);
    }

    and(count) {
        const bars = new ColorPicker(this.colors, this.lineOrder, count, this.lineCount);

        const lines = new ColorPicker(this.colors, this.lineOrder, this.barCount, count);

        return {
            bars,
            lines,
            bar: bars,
            line: lines
        };
    }
}

class BenchmarkingGaugeColorPicker extends ColorPicker {
    constructor() {
        super(
            [
                colors.vividTangerine,
                colors.tacao,
                colors.cherokee,
                colors.dollarBill,
                colors.chicago
            ],
            [0, 1, 2, 3, 4]
        );
    }
}

class GaugeBarsColorPicker extends ColorPicker {
    constructor() {
        super(
            [
                colors.vividTangerine,
                colors.tacao,
                colors.cherokee,
                colors.dollarBill,
                colors.chicago
            ],
            [0, 1, 2, 3, 4]
        );
    }
}

class MilkingColorPicker extends ColorPicker {
    constructor() {
        super(
            [colors.milking, colors.green, colors.maroon, colors.amber, colors.lilac],
            [2, 3, 4, 1, 0]
        );
    }
}

class FatProteinColorPicker extends ColorPicker {
    constructor() {
        super(
            [
                colors.googleBlue700,
                colors.googleBlue100,
                colors.googleGreen700,
                colors.googleGreen100,
                colors.paperAmber700,
                colors.paperAmber100,
                colors.maroon
            ],
            [0, 1, 2, 3, 4, 5, 6]
        );
    }
}

class FeedingColorPicker extends ColorPicker {
    constructor() {
        super(
            [colors.feeding, colors.orange, colors.green, colors.lilac, colors.cyan],
            [1, 2, 3, 4, 0]
        );
    }
}

class HealthColorPicker extends ColorPicker {
    constructor() {
        super(
            [colors.health, colors.seafoam, colors.amber, colors.blue, colors.silver],
            [3, 4, 2, 1, 0]
        );
    }
}

class CullingColorPicker extends ColorPicker {
    constructor() {
        super(
            [colors.culling, colors.maroon, colors.silver, colors.blue, colors.cyan],
            [4, 2, 3, 1, 0]
        );
    }
}

class ReproductionColorPicker extends ColorPicker {
    constructor() {
        super(
            [colors.reproduction, colors.cyan, colors.orange, colors.seafoam, colors.bronze],
            [4, 1, 3, 2, 0]
        );
    }
}

class FinancialColorPicker extends ColorPicker {
    constructor() {
        super(
            [colors.financial, colors.bronze, colors.maroon, colors.amber, colors.blue],
            [2, 4, 3, 1, 0]
        );
    }
}

class HerdInfoPermissionColorPicker extends ColorPicker {
    constructor() {
        super(
            [
                colors.cherokee,
                colors.tacao,
                colors.driftwood,
                colors.darkBrown,
                colors.deepTeal,
                colors.genoa,
                colors.persianGreen,
                colors.fern,
                colors.caper,
                colors.tiara
            ],
            [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
        );
    }
}

class AuditPermissionColorPicker extends ColorPicker {
    constructor() {
        super(
            [
                colors.tiara,
                colors.caper,
                colors.fern,
                colors.genoa,
                colors.regalBlue,
                colors.revolver,
                colors.meteorite,
                colors.darkPurple,
                colors.bouquet,
                colors.melon,
                colors.googleBlue100
            ],
            [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
        );
    }
}

class ProductionColorPicker extends ColorPicker {
    constructor() {
        super(
            [
                colors.complementaryBlue,
                colors.googleBlue700,
                colors.googleBlue100,
                colors.clover,
                colors.success,
                colors.caper
            ],
            [0, 1, 2, 3, 4, 5]
        );
    }
}

class AuditPermissionPortfolioColorPicker extends ColorPicker {
    constructor() {
        super(
            [
                colors.sprout,
                colors.creamBrulee,
                colors.saffron,
                colors.corvette,
                colors.seaPink,
                colors.googleBlue100,
                colors.googleBlue300,
                colors.googleBlue700,
                colors.gulfStrem,
                colors.peppermint,
                colors.red,
                colors.dustyGray
            ],
            [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
        );
    }
}

class FeedKingCardsColorPicker extends ColorPicker {
    constructor() {
        super(
            [
                feedKingCardsColors.darkSlateGray,
                feedKingCardsColors.crystalBlue,
                feedKingCardsColors.crystal,
                feedKingCardsColors.japaneseViolet,
                feedKingCardsColors.frenchLilac,
                feedKingCardsColors.pinkLavander,
                feedKingCardsColors.bronze,
                feedKingCardsColors.middleYellowRed,
                feedKingCardsColors.tan,
                feedKingCardsColors.darkCornflowerBlue,
                feedKingCardsColors.hanBlue,
                feedKingCardsColors.lavanderBlue,
                feedKingCardsColors.smokeyTopaz,
                feedKingCardsColors.darkPastelRed,
                feedKingCardsColors.paleChestnut,
                feedKingCardsColors.yellow,
                feedKingCardsColors.japaneseLaurel,
                feedKingCardsColors.apple,
                feedKingCardsColors.lightMossGreen,
                feedKingCardsColors.graniteGray,
                feedKingCardsColors.ripeMango,
                feedKingCardsColors.chelseaCucumber,
                feedKingCardsColors.elm
            ],
            [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22]
        );
    }
}

export const benchmarkingGaugeColorPicker = {
    green: '#bcd5a3',
    yellow: '#fed999',
    orange: '#f57521',
    red: '#faa69a',
    blue1: '#A0C6E8',
    blue2: '#6FA9DD',
    blue3: '#3D85C7',
    blue4: '#0A5495'
};

export const feedKingCardsColors = {
    darkSlateGray: '#274856',
    crystalBlue: '#6a99ad',
    crystal: '#a9cfdf',
    japaneseViolet: '#5C2A5D',
    frenchLilac: '#956196',
    pinkLavander: '#d1b2d1',
    bronze: '#DA7C2D',
    middleYellowRed: '#E9AE7D',
    tan: '#D1B299',
    darkCornflowerBlue: '#1c4587',
    hanBlue: '#3c78d8',
    lavanderBlue: '#c9daf8',
    smokeyTopaz: '#85200c',
    darkPastelRed: '#cc4125',
    paleChestnut: '#e6b8af',
    yellow: '#f9cb28',
    japaneseLaurel: '#38761d',
    apple: '#6aa84f',
    lightMossGreen: '#b6d7a8',
    graniteGray: '#666666',
    ripeMango: '#f8cb27',
    chelseaCucumber: '#83B24C',
    elm: '#22918C',

    chart: {
        get chartColors() {
            return new FeedKingCardsColorPicker();
        }
    }
};

export const colors = {
    // Text
    primaryText: 'rgba(0,0,0,0.87)',
    secondaryText: 'rgba(0,0,0,0.54)',
    disabledText: 'rgba(0,0,0,0.38)',
    divider: 'rgba(0,0,0,0.12)',
    // Keys
    red: '#be2005',
    pink: '#f52267',
    purple: '#7c1e6B',
    midnight: '#1b1850',
    sky: '#0088bb',
    emerald: '#367f57',
    // Complementary
    maroon: '#610606',
    orange: '#f56207',
    amber: ['#eb9500', '#cf8300'],
    bronze: '#5c4e00',
    green: '#5ba210',
    cyan: ['#22cbce', '#00a4b3'],
    blue: '#0c439c',
    lilac: '#d149d1',
    seafoam: ['#54cc9e', '#31a679'],
    silver: '#8e919e',
    olive: '#B5CA8D',
    dulux: '#AA8253',
    karaka: '32321E',
    clover: '#3E5D1B',
    bondiBlue: '#0FA3B1',
    taxBreak: '#426B69',
    dallas: '#5D482A',
    olivine: '#8BB174',
    tiara: '#C0D9D4',
    caper: '#ADCB8B',
    fern: '#79B371',
    genoa: '#1E6B69',
    regalBlue: '#014F70',
    revolver: '#37122F',
    meteorite: '#421D7D',
    darkPurple: '#7D1D6B',
    bouquet: '#A17BA4',
    melon: '#FEB8B8',
    cherokee: '#FDDB99',
    tacao: '#F9B071',
    driftwood: '#B9834F',
    darkBrown: '#644724',
    deepTeal: '#01352E',
    persianGreen: '#06A293',
    chicago: '#5E5E5E',
    googleBlue100: '#C6DAFC',
    googleBlue200: '#A1C2FA',
    googleBlue300: '#7baaf7',
    googleBlue400: '#6999EF',
    googleBlue500: '#5789E7',
    googleBlue600: '#4578DE',
    googleBlue700: '#3367D6',
    googleBlue800: '#2a56c6',
    googleBlue900: '#1c3aa9',
    googleGreen100: '#B7E1CD',
    googleGreen700: '#0B8043',
    paperAmber100: '#FFECB3',
    paperAmber700: '#FFA000',
    yellow900: '#FFD400',
    yellow700: '#FFDD3C',
    yellow500: '#FFEA61',
    yellow300: '#FFF192',
    yellow100: '#FFFFB7',
    complementaryBlue: '#0C439C',
    vividTangerine: '#FA9D8F',
    dollarBill: '#8CB563',
    havelockBlue: '#739DE1',
    regentStBlue: '#AAC6E3',
    linkWater: '#D2E2F0',
    spindle: '#B6D3ED',
    sprout: '#b7d6a8',
    creamBrulee: '#ffe598',
    saffron: '#f1c231',
    corvette: '#f9cb9c',
    seaPink: '#ea9999',
    dustyGray: '#999999',
    gulfStrem: '#76a5ae',
    peppermint: '#d9ead3',
    rajah: '#f2b470',
    pineGlade: '#A9CE91',
    fernGreen: '#548235',
    champagne: '#FBE5D6',
    apricotPeach: '#F8CBAD',
    jaffa: '#ED7D31',
    desert: '#B25E25',
    chelseaCucumber: '#6AA84F',
    dandelion: '#FFD966',
    sunglo: '#E06666',
    zanah: '#DAECD6',
    pigletSnout: '#ECC3BF',
    denim: '#1166DA',
    indicatorTrendGreen: '#83b24cff',
    indicatorTrendGreenBackground: '#d9e9d3ff',
    indicatorTrendRed: '#cc0100ff',
    indicatorTrendRedBackground: '#f4cbccff',
    indicatorTrendGray: '#909090',
    indicatorTrendGrayBackground: '#E8E8E8',
    indicatorTrendYellow: '#f9cb28',

    get gaugeBars() {
        return colors.regalBlue;
    },
    get culling() {
        return colors.red;
    },
    get reproduction() {
        return colors.pink;
    },
    get health() {
        return colors.purple;
    },
    get feeding() {
        return colors.midnight;
    },
    get milking() {
        return colors.sky;
    },
    get weights() {
        return colors.sky;
    },
    get components() {
        return colors.sky;
    },
    get quality() {
        return colors.sky;
    },
    get financial() {
        return colors.emerald;
    },
    get herd_info() {
        return colors.deepTeal;
    },
    get audit() {
        return colors.regalBlue;
    },

    _toNameDict: {},
    toName(hex) {
        if (!colors._toNameDict[hex]) {
            Object.keys(colors).some((key) => {
                if (
                    [
                        'culling',
                        'reproduction',
                        'health',
                        'feeding',
                        'milking',
                        'weights',
                        'components',
                        'quality',
                        'financial',
                        'gauge'
                    ].indexOf(key) > -1
                ) {
                    return false;
                }

                const value = colors[key];
                let match = hex === colors[key];
                if (!match && Array.isArray(value)) {
                    match = value[0] === hex || value[1] === hex;
                }

                if (match) {
                    if (Array.isArray(hex)) {
                        hex.forEach((h) => (colors._toNameDict[h] = key));
                    }

                    colors._toNameDict[hex] = key;
                    return true;
                } else {
                    return false;
                }
            });
        }

        return colors._toNameDict[hex];
    },
    // Charts
    gridLine: 'rgba(0,0,0,0.15)',
    chart: {
        get benchmarkingGuageColors() {
            return new GaugeBarsColorPicker();
        },
        get gaugeBars() {
            return new GaugeBarsColorPicker();
        },
        get weights() {
            return new MilkingColorPicker();
        },
        get components() {
            return new MilkingColorPicker();
        },
        get quality() {
            return new MilkingColorPicker();
        },
        get feeding() {
            return new FeedingColorPicker();
        },
        get health() {
            return new HealthColorPicker();
        },
        get culling() {
            return new CullingColorPicker();
        },
        get reproduction() {
            return new ReproductionColorPicker();
        },
        get financial() {
            return new FinancialColorPicker();
        },
        get herd_info() {
            return new HerdInfoPermissionColorPicker();
        },
        get audit() {
            return new AuditPermissionColorPicker();
        },
        get fatProtein() {
            return new FatProteinColorPicker();
        },
        get production() {
            return new ProductionColorPicker();
        },
        get auditPortfolio() {
            return new AuditPermissionPortfolioColorPicker();
        }
    },
    // Gauges
    success: '#78aa42',
    successLight: 'rgba(120,170,66,0.5)',
    warning: '#fbb514',
    warningLight: 'rgba(251,181,20,0.5)',
    danger: '#f57520',
    dangerLight: 'rgba(245,117,32,0.5)',
    error: '#f0492d',
    errorLight: 'rgba(240,73,45,0.5)',
    noColor: 'rgba(0,0,0,0.12)',
    gaugeSuccessColor(reverseColoring: boolean) {
        return reverseColoring ? colors.error : colors.success;
    },
    gaugeSuccessLightColor(reverseColoring: boolean) {
        return reverseColoring ? colors.errorLight : colors.successLight;
    },
    gaugeWarningColor(reverseColoring: boolean) {
        return reverseColoring ? colors.danger : colors.warning;
    },
    gaugeWarningLightColor(reverseColoring: boolean) {
        return reverseColoring ? colors.dangerLight : colors.warningLight;
    },
    gaugeDangerColor(reverseColoring: boolean) {
        return reverseColoring ? colors.warning : colors.danger;
    },
    gaugeDangerLightColor(reverseColoring: boolean) {
        return reverseColoring ? colors.warningLight : colors.dangerLight;
    },
    gaugeErrorColor(reverseColoring: boolean) {
        return reverseColoring ? colors.success : colors.error;
    },
    gaugeErrorLightColor(reverseColoring: boolean) {
        return reverseColoring ? colors.successLight : colors.errorLight;
    },

    colorForType(
        type: string,
        bottomThreshold: number,
        topThreshold: number,
        value: number | null,
        reverseColoring: boolean | undefined,
        gaugeDynamicColoring: string[] | undefined
    ) {
        if (gaugeDynamicColoring && gaugeDynamicColoring.length) {
            switch (type) {
                case 'min':
                    return value && bottomThreshold <= value && topThreshold > value
                        ? gaugeDynamicColoring[0]
                        : gaugeDynamicColoring[0];
                case 'warn':
                    return value && bottomThreshold <= value && topThreshold > value
                        ? gaugeDynamicColoring[1]
                        : gaugeDynamicColoring[1];
                case 'danger':
                    return value && bottomThreshold <= value && topThreshold > value
                        ? gaugeDynamicColoring[2]
                        : gaugeDynamicColoring[2];
                case 'error':
                    return value && bottomThreshold <= value && topThreshold > value
                        ? gaugeDynamicColoring[3]
                        : gaugeDynamicColoring[3];
                case 'max':
                case 'none':
                    return colors.noColor;
                default:
                    return colors.noColor;
            }
        } else {
            switch (type) {
                case 'min':
                    return value && bottomThreshold <= value && topThreshold > value
                        ? this.gaugeSuccessColor(reverseColoring)
                        : this.gaugeSuccessLightColor(reverseColoring);
                case 'warn':
                    return value && bottomThreshold <= value && topThreshold > value
                        ? this.gaugeWarningColor(reverseColoring)
                        : this.gaugeWarningLightColor(reverseColoring);
                case 'danger':
                    return value && bottomThreshold <= value && topThreshold > value
                        ? this.gaugeDangerColor(reverseColoring)
                        : this.gaugeDangerLightColor(reverseColoring);
                case 'error':
                    return value && bottomThreshold <= value && topThreshold > value
                        ? this.gaugeErrorColor(reverseColoring)
                        : this.gaugeErrorLightColor(reverseColoring);
                case 'max':
                case 'none':
                    return colors.noColor;
                default:
                    return colors.noColor;
            }
        }
    }
};
